import { jsPDF } from "jspdf";

const INK = [34, 35, 50];
const LILAC = [154, 131, 216];
const GRAY = [112, 114, 132];
const LINE = [228, 226, 240];

// Branded, text-based PDF of the generated launch proposal.
export function downloadProposalPdf({ proposal, number, contact, money }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const M = 48;
  let y = 0;

  const ensureSpace = (h) => {
    if (y + h > 800) {
      doc.addPage();
      y = 56;
    }
  };

  // Header band
  doc.setFillColor(184, 167, 232);
  doc.rect(0, 0, W, 92, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...INK);
  doc.text("pepmuse", M, 42);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("YOUR CUSTOM PROPOSAL", M, 62);
  doc.setFontSize(10);
  doc.text(`#${number}`, W - M, 42, { align: "right" });

  y = 128;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...INK);
  doc.text("What's left to launch.", M, y);
  y += 20;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...GRAY);
  const prepared = contact.businessName?.trim() || contact.name?.trim() || "Your brand";
  doc.text(`${proposal.date}  ·  Prepared for ${prepared}`, M, y);
  const contactLine = [contact.email?.trim(), contact.phone?.trim()].filter(Boolean).join("  ·  ");
  if (contactLine) {
    y += 14;
    doc.text(contactLine, M, y);
  }
  y += 30;

  proposal.groups.forEach((group) => {
    const groupTotal = group.neededItems.reduce((s, i) => s + i.price, 0);
    ensureSpace(80);
    doc.setDrawColor(...LINE);
    doc.setLineWidth(1);
    doc.line(M, y, W - M, y);
    y += 18;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...INK);
    doc.text(group.label, M, y);
    doc.setTextColor(...LILAC);
    doc.text(money(groupTotal), W - M, y, { align: "right" });
    y += 18;
    group.neededItems.forEach((item) => {
      ensureSpace(18);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...GRAY);
      doc.text(item.label, M, y);
      doc.setTextColor(...INK);
      doc.text(money(item.price), W - M, y, { align: "right" });
      y += 16;
    });
    y += 12;
  });

  ensureSpace(150);
  y += 4;
  doc.setDrawColor(...INK);
  doc.setLineWidth(1.2);
  doc.line(M, y, W - M, y);
  y += 20;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...GRAY);
  doc.text("Subtotal", M, y);
  doc.setTextColor(...INK);
  doc.text(money(proposal.subtotal), W - M, y, { align: "right" });
  y += 18;

  if (proposal.discount > 0) {
    doc.setTextColor(...GRAY);
    doc.text("Package discount", M, y);
    doc.setTextColor(...LILAC);
    doc.text(`-${money(proposal.discount)}`, W - M, y, { align: "right" });
    y += 18;
  }
  if (proposal.minimumApplied) {
    doc.setFontSize(8.5);
    doc.setTextColor(...GRAY);
    doc.text(`Minimum engagement of ${money(4997)} applies.`, M, y);
    y += 18;
    doc.setFontSize(11);
  }

  y += 4;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...INK);
  doc.text("Estimated investment", M, y + 6);
  doc.setFontSize(22);
  doc.setTextColor(...LILAC);
  doc.text(money(proposal.totalPrice), W - M, y + 6, { align: "right" });
  y += 34;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...GRAY);
  const note = doc.splitTextToSize(
    "One-time estimate based on the checklist items not yet in place, using PepMuse standard per-service pricing. Package pricing is separate: Business in a Box bundles the core launch scope for $9,997. Final scope and quote are confirmed together in writing — nothing is due now. pepmuse · Your peptide brand starts here.",
    W - 2 * M
  );
  doc.text(note, M, y);

  doc.save(`pepmuse-proposal-${number}.pdf`);
}

const SEV_COLORS = {
  critical: [220, 90, 90],
  high: [226, 156, 110],
  medium: [220, 194, 122],
  low: [130, 172, 214],
};

// Branded PDF report for a compliance scan result.
export function exportCompliancePdf(result) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const M = 48;
  let y = 0;
  const gradeColor = { A: [110, 190, 150], B: [154, 131, 216], C: [220, 194, 122], D: [226, 156, 110], F: [220, 90, 90] }[result.grade] || LILAC;

  const ensureSpace = (h) => {
    if (y + h > 800) {
      doc.addPage();
      y = 56;
    }
  };
  const wrapped = (text, size, color, bold = false, indent = 0) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text, W - 2 * M - indent);
    lines.forEach((line) => {
      ensureSpace(size + 6);
      doc.text(line, M + indent, y);
      y += size + 5;
    });
  };

  doc.setFillColor(184, 167, 232);
  doc.rect(0, 0, W, 92, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...INK);
  doc.text("pepmuse", M, 42);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("COMPLIANCE SCAN REPORT", M, 62);

  y = 126;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...gradeColor);
  doc.text(`Score ${result.score} / 100  ·  Grade ${result.grade}`, M, y);
  y += 22;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...GRAY);
  doc.text(doc.splitTextToSize(`${result.target} · ${result.pages_scanned} page(s) · ${result.scanned_at?.slice(0, 10) || ""}`, W - 2 * M), M, y);
  y += 26;
  wrapped(
    `Flags: ${result.summary.critical} critical · ${result.summary.high} high · ${result.summary.medium} medium · ${result.summary.low} low`,
    10,
    INK,
    true
  );
  y += 8;

  result.flags.forEach((f) => {
    ensureSpace(90);
    doc.setDrawColor(...LINE);
    doc.setLineWidth(1);
    doc.line(M, y, W - M, y);
    y += 16;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...(SEV_COLORS[f.severity] || INK));
    doc.text(`[${f.severity.toUpperCase()}]`, M, y);
    doc.setTextColor(...INK);
    doc.text(f.title, M + 78, y);
    y += 16;
    if (f.detail) wrapped(f.detail, 9.5, GRAY, false, 0);
    if (f.evidence) wrapped(`Found: "${f.evidence}"`, 9, GRAY);
    wrapped(`Fix: ${f.fix}`, 9.5, INK, true);
    y += 10;
  });

  if (result.passed.length) {
    ensureSpace(60);
    doc.setDrawColor(...LINE);
    doc.line(M, y, W - M, y);
    y += 18;
    wrapped("Passing checks", 12, INK, true);
    y += 4;
    result.passed.forEach((p) => wrapped(`• ${p.title}`, 9.5, GRAY));
  }

  y += 12;
  wrapped(
    "Automated scan by PepMuse — a limited review of on-page language against common risk patterns. It can miss issues and is not legal advice or a compliance determination; have qualified counsel review your site before launch. pepmuse · Your peptide brand starts here.",
    8.5,
    GRAY
  );

  doc.save(`pepmuse-compliance-grade-${result.grade}.pdf`);
}
